import * as Sentry from '@sentry/react';

/**
 * Sentry 初始化狀態
 */
let sentryInitialized = false;

/**
 * 初始化 Sentry
 */
export function initSentry(): void {
  // 只在 production 或 staging 環境初始化 Sentry
  if (import.meta.env.MODE === 'development') {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('[Sentry] VITE_SENTRY_DSN 未設定，跳過 Sentry 初始化');
    return;
  }

  if (sentryInitialized) {
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE || 'production',
      tracesSampleRate: import.meta.env.MODE === 'production' ? 1.0 : 1.0, // 100% sample rate
      release: import.meta.env.VITE_GIT_SHA || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
      ],
      beforeSend(event, _hint) {
        // 過濾敏感資訊
        if (event.request) {
          // 移除 Authorization header
          if (event.request.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers['X-Line-ID-Token'];
          }
        }

        // 移除敏感資料
        if (event.user) {
          // 不記錄完整的 user_id（可能包含敏感資訊）
          if (event.user.id) {
            const userId =
              typeof event.user.id === 'string'
                ? event.user.id
                : String(event.user.id);
            event.user.id = userId.substring(0, 8) + '...';
          }
        }

        return event;
      },
    });

    sentryInitialized = true;
    console.log('[Sentry] 初始化成功');
  } catch (error) {
    console.error('[Sentry] 初始化失敗:', error);
  }
}

/**
 * 捕獲異常
 *
 * @param error - 錯誤物件
 * @param context - 額外上下文資訊
 */
export function captureException(
  error: unknown,
  context?: {
    level?: 'error' | 'warning' | 'info';
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    user?: {
      id?: string;
      tenantId?: string;
    };
  }
): void {
  if (!sentryInitialized) {
    return;
  }

  try {
    Sentry.withScope(scope => {
      // 設定錯誤級別
      if (context?.level) {
        scope.setLevel(context.level as Sentry.SeverityLevel);
      }

      // 設定標籤
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // 設定額外資訊
      if (context?.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      // 設定使用者資訊
      if (context?.user) {
        scope.setUser({
          id: context.user.id,
          tenantId: context.user.tenantId,
        });
      }

      // 捕獲錯誤
      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(String(error), context?.level || 'error');
      }
    });
  } catch (sentryError) {
    // Sentry 錯誤不應該影響應用程式運行
    console.error('[Sentry] 捕獲錯誤失敗:', sentryError);
  }
}

/**
 * 捕獲訊息
 *
 * @param message - 訊息內容
 * @param context - 額外上下文資訊
 */
export function captureMessage(
  message: string,
  context?: {
    level?: 'error' | 'warning' | 'info';
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    user?: {
      id?: string;
      tenantId?: string;
    };
  }
): void {
  if (!sentryInitialized) {
    return;
  }

  try {
    Sentry.withScope(scope => {
      // 設定錯誤級別
      if (context?.level) {
        scope.setLevel(context.level as Sentry.SeverityLevel);
      }

      // 設定標籤
      if (context?.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }

      // 設定額外資訊
      if (context?.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      // 設定使用者資訊
      if (context?.user) {
        scope.setUser({
          id: context.user.id,
          tenantId: context.user.tenantId,
        });
      }

      Sentry.captureMessage(message, context?.level || 'info');
    });
  } catch (sentryError) {
    // Sentry 錯誤不應該影響應用程式運行
    console.error('[Sentry] 捕獲訊息失敗:', sentryError);
  }
}
